import { Button, ConfirmModal } from '@vrplatform/components';
import {
  type ApiDeleteError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { showSuccessNotification } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import { Modal } from '@mantine/core';
import { Box } from '@mantine/core';
import { Group } from '@mantine/core';
import { Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { type ComponentProps, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { OwnerEmailInput } from '../OwnerEditForm';
import type { OwnerDetail } from '../useOwnerDetailDrawerQuery';
import { formatOwnerName } from '@vrplatform/ui-common';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  owner: Maybe<{
    id: string;
    firstName: Maybe<string>;
    name: Maybe<string>;
    email: Maybe<string>;
    isArchived: boolean;
    tenantId: string;
    userAccess: OwnerDetail['userAccess'];
  }>;
}

export const OwnerEllipsisMenuModals = ({
  deleteModal,
  archiveModal,
  owner: initial,
  emailModal,
  copyLinkModal,
  revokeAccessModal,
}: {
  owner: ModalProps['owner'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
    onSuccess?: () => void;
  };
  archiveModal: {
    opened: boolean;
    closeModal: () => void;
  };
  emailModal: {
    opened: boolean;
    closeModal: () => void;
  };
  copyLinkModal: {
    opened: boolean;
    closeModal: () => void;
  };
  revokeAccessModal: {
    opened: boolean;
    closeModal: () => void;
  };
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const owner = initial || debounced;

  const hasEmail = !!(owner?.userAccess?.email || owner?.email);

  return (
    <>
      <Archive {...archiveModal} owner={owner} />
      <Delete {...deleteModal} owner={owner} />
      <Email
        {...emailModal}
        opened={emailModal.opened && hasEmail}
        owner={owner}
      />
      <CopyLink
        {...copyLinkModal}
        opened={copyLinkModal.opened && hasEmail}
        owner={owner}
      />
      <RevokeAccess
        {...revokeAccessModal}
        opened={revokeAccessModal.opened}
        owner={owner}
      />

      <MissingEmail
        opened={(emailModal.opened || copyLinkModal.opened) && !hasEmail}
        closeModal={() => {
          emailModal.closeModal();
          copyLinkModal.closeModal();
        }}
        type={emailModal.opened ? 'email' : 'copy-link'}
        owner={owner}
      />
    </>
  );
};

const Archive = ({ closeModal, opened, owner }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'put',
    '/contacts/{id}'
  );

  const ownerId = owner?.id;
  const ownerName = formatOwnerName(owner || undefined, {
    lastNameFirst: false,
    showEmpty: false,
  });
  const isArchived = owner?.isArchived;

  const archiveOwner = async () => {
    if (!ownerId)
      return {
        isSuccess: false,
        message: 'Owner ID is missing',
      };

    await mutateAsync({
      params: {
        path: {
          id: ownerId,
        },
      },
      body: {
        status: !isArchived ? 'inactive' : 'active',
      },
    })
      .then(() => {
        invalidate();
        closeModal();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'We failed to update the owner',
        });
      });
  };

  return (
    <ConfirmModal
      type="confirm"
      onSubmit={archiveOwner}
      opened={opened}
      icon="FolderOpenIcon"
      loading={loading}
      closeModal={closeModal}
      title={`Are you sure you want to ${!isArchived ? 'archive' : 'unarchive'} ${ownerName || 'this owner'}?`}
      submitTitle={!isArchived ? 'Archive' : 'Unarchive'}
      subtitle={
        !isArchived
          ? 'The owner will be archived and hidden behind a table filter. Owners users will retain access to the data.'
          : "The owner will be unarchived and visible in the owner's table."
      }
    />
  );
};

const Delete = ({
  closeModal,
  opened,
  owner,
  onSuccess,
}: ModalProps & {
  onSuccess?: () => void;
}) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/contacts/{id}'
  );

  const ownerId = owner?.id;
  const ownerName = formatOwnerName(owner || undefined, {
    lastNameFirst: false,
    showEmpty: false,
  });
  const tenantId = owner?.tenantId;

  const submit: ComponentProps<typeof ConfirmModal>['onSubmit'] = useCallback(
    async (isSecondConfirm: boolean) => {
      if (!ownerId || !tenantId) return;

      return await mutateAsync({
        params: {
          path: {
            id: ownerId,
          },
          query: {
            onLocked: isSecondConfirm ? 'archive' : 'error',
          },
        },
      })
        .then((res) => {
          showSuccessNotification({
            title: 'Success!',
            message: `Owner was ${res.data.status} successfully.`,
          });

          onSuccess?.();
          closeModal();
          invalidate();
        })
        .catch((error: ApiDeleteError) => {
          if (error.context?.canArchive) {
            return {
              showConfirm: true,
            };
          }

          showApiErrorNotification({
            error,
            title: 'Failed to delete owner',
          });
        });
    },
    [closeModal, invalidate, mutateAsync, ownerId, tenantId, onSuccess]
  );

  return (
    <ConfirmModal
      closeModal={closeModal}
      onSubmit={submit}
      opened={opened}
      subtitle="The owner will be entirely removed from your team."
      title={`Are you sure you want to delete ${ownerName || 'this owner'}?`}
      loading={loading}
      type="delete"
      secondConfirm={{
        title: 'We have found existing related records to this owner',
        subtitle:
          'You can archive the owner instead, which will hide the owner from the owner table.',
        submitTitle: 'Archive',
      }}
    />
  );
};

const Email = ({ closeModal, opened, owner }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'post',
    '/contacts/{id}/invite'
  );

  const ownerId = owner?.id;
  const ownerName = formatOwnerName(owner || undefined, {
    lastNameFirst: false,
    showEmpty: false,
  });
  const tenantId = owner?.tenantId;

  const submit = useCallback(async () => {
    if (!ownerId || !tenantId) return;

    return await mutateAsync({
      params: {
        path: {
          id: ownerId,
        },
      },
      body: {
        email: true,
      },
    })
      .then(() => {
        showSuccessNotification({
          title: 'Success!',
          message: 'Email invitation was sent successfully.',
        });

        closeModal();
        invalidate();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to send email invitation',
        });
      });
  }, [closeModal, invalidate, mutateAsync, ownerId, tenantId]);

  const isReinvite = (owner?.userAccess?.status ?? 'inactive') !== 'inactive';

  return (
    <ConfirmModal
      closeModal={closeModal}
      onSubmit={submit}
      opened={opened}
      size={500}
      icon="EmailIcon"
      title={
        isReinvite
          ? 'Would you like to re-send an email invitation to'
          : 'Would you like to send an email invitation to'
      }
      entityTitle={ownerName}
      subtitle="We will send an email invitation to the owner and they will have 7 days to accept the invitation and join the owner portal."
      loading={loading}
      type="confirm"
    />
  );
};

const CopyLink = ({ closeModal, opened, owner }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'post',
    '/contacts/{id}/invite'
  );

  const ownerId = owner?.id;
  const ownerName = formatOwnerName(owner || undefined, {
    lastNameFirst: false,
    showEmpty: false,
  });
  const tenantId = owner?.tenantId;

  const submit = useCallback(async () => {
    if (!ownerId || !tenantId) return;

    return await mutateAsync({
      params: {
        path: {
          id: ownerId,
        },
      },
      body: {
        email: false,
      },
    })
      .then((result) => {
        navigator.clipboard.writeText(result.data.url).then(() => {
          showSuccessNotification({
            message: 'Invitation link was copied to your clipboard.',
          });

          closeModal();
          invalidate();
        });
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to copy invitation link',
        });
      });
  }, [closeModal, invalidate, mutateAsync, ownerId, tenantId]);

  return (
    <ConfirmModal
      closeModal={closeModal}
      onSubmit={submit}
      opened={opened}
      size={500}
      icon="EmailIcon"
      title={'Would you like to copy the invitation link for'}
      entityTitle={ownerName}
      subtitle="You can share the invitation link with the owner to join the owner portal and they have 7 days to accept the invitation link."
      loading={loading}
      type="confirm"
    />
  );
};

const RevokeAccess = ({ closeModal, opened, owner }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'patch',
    '/contacts/{id}/revoke-access'
  );

  const ownerId = owner?.id;
  const ownerName = formatOwnerName(owner || undefined, {
    lastNameFirst: false,
    showEmpty: false,
  });
  const tenantId = owner?.tenantId;

  const submit = useCallback(async () => {
    if (!ownerId || !tenantId) return;

    return await mutateAsync({
      params: {
        path: {
          id: ownerId,
        },
      },
    })
      .then(() => {
        showSuccessNotification({
          message: 'Owner portal access was revoked successfully.',
        });

        closeModal();
        invalidate();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to revoke owner portal access',
        });
      });
  }, [closeModal, invalidate, mutateAsync, ownerId, tenantId]);

  return (
    <ConfirmModal
      closeModal={closeModal}
      onSubmit={submit}
      opened={opened}
      size={500}
      icon="CrossCircleIcon"
      title={'Would you like to revoke owner portal access for'}
      entityTitle={ownerName}
      subtitle="The owner will continue to show in your dashboard, but they will no longer have access to the owner portal."
      loading={loading}
      type="warning"
      submitTitle="Yes, revoke access"
    />
  );
};

const MissingEmail = ({
  closeModal,
  opened,
  owner,
  type,
}: ModalProps & {
  type: 'email' | 'copy-link';
}) => {
  const invalidate = useInvalidateApiQueries(['owners']);

  type FormInputs = {
    email: string;
  };

  const email = owner?.userAccess?.email || owner?.email || '';

  const methods = useForm<FormInputs>({
    values: {
      email,
    },
  });

  const { mutateAsync } = useApiMutation('put', '/contacts/{id}');

  const ownerId = owner?.id;
  const tenantId = owner?.tenantId;

  const submit = useCallback(
    async (data: FormInputs) => {
      if (!ownerId || !tenantId) return;

      return await mutateAsync({
        params: {
          path: {
            id: ownerId,
          },
        },
        body: {
          email: data.email,
        },
      })
        .then(() => {
          showSuccessNotification({
            title: 'Success!',
            message: 'Email invitation was sent successfully.',
          });

          closeModal();
          invalidate();
        })
        .catch((error) => {
          showApiErrorNotification({
            error,
            title: 'Failed to send email invitation',
          });
        });
    },
    [closeModal, invalidate, mutateAsync, ownerId, tenantId]
  );

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      size={500}
      title={'Invite your owner to the owner portal'}
    >
      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        <Text c="neutral" mb="md">
          We noticed you haven't added an email to this owner. Please add an
          email to continue.
        </Text>
        <FormProvider {...methods}>
          <Box mb={'xl'}>
            <OwnerEmailInput isEmailDisabled={false} forceRequired />
          </Box>
          <Group justify="right" my={5}>
            <Button type="reset" disabled={methods.formState.isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={methods.formState.isSubmitting}
              leftIcon={type === 'copy-link' ? 'Link2Icon' : 'Send2Icon'}
            >
              {type === 'copy-link' ? 'Copy Link' : 'Send Invite'}
            </Button>
          </Group>
        </FormProvider>
      </Box>
    </Modal>
  );
};
